import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, LITURGY_LABEL } from "@/lib/api";
import { useLiturgical } from "@/context/LiturgicalContext";
import AudioPlayer from "@/components/AudioPlayer";
import NewsletterForm from "@/components/NewsletterForm";
import QuoteRotator from "@/components/QuoteRotator";

const formatDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};

const Home = () => {
    const { today, loading } = useLiturgical();
    const [featured, setFeatured] = useState(null);

    useEffect(() => {
        api.get("/prayers/featured").then(r => setFeatured(r.data)).catch(()=>{});
    }, []);

    if (loading || !today) return <div className="max-w-4xl mx-auto px-6 py-32 text-center text-muted-foreground" data-testid="loading">Preparing today's page…</div>;

    const heroBg = today.mood === "sacred"
        ? "https://images.unsplash.com/photo-1576016770956-debb63d92058?w=1600&q=80"
        : "https://images.unsplash.com/photo-1473177104440-ffee2f376098?w=1600&q=80";

    return (
        <div className="animate-fade-in" data-testid="home-page">
            {/* ROTATING QUOTE — visible immediately below the top banner */}
            <section className="border-b border-border/40 bg-parchment-50/40" data-testid="home-quote-section">
                <div className="max-w-4xl mx-auto px-6 md:px-10 pt-10 pb-8">
                    <div className="small-caps text-[10px] text-muted-foreground text-center mb-4 tracking-widest">Wisdom of the Church</div>
                    <QuoteRotator intervalMs={25000} />
                </div>
            </section>

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-border">
                <div className="absolute inset-0 -z-0">
                    <img src={heroBg} alt="" className="w-full h-full object-cover opacity-30" />
                    <div className={`absolute inset-0 ${today.mood === "sacred" ? "bg-shroud-900/70" : "bg-parchment-50/60"}`} />
                </div>
                <div className="relative max-w-6xl mx-auto px-6 md:px-10 pt-20 pb-24 md:pt-28 md:pb-32 text-center">
                    <div className="small-caps text-xs text-muted-foreground mb-6" data-testid="hero-date">{formatDate(today.date)}</div>
                    <div className="flex items-center justify-center gap-3 mb-8">
                        <span className="w-3 h-3 rounded-full bg-liturgy" />
                        <span className="small-caps text-xs accent-liturgy" data-testid="hero-color">{LITURGY_LABEL[today.color]} · {today.season}</span>
                        <span className="w-3 h-3 rounded-full bg-liturgy" />
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1]" data-testid="hero-title">
                        {today.feast || (today.season === "Ordinary Time" ? "A day for the ordinary work of love" : `A day within ${today.season}`)}
                    </h1>
                    <p className="mt-8 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground leading-relaxed font-body italic">
                        “{today.readings?.reflection}”
                    </p>
                    <div className="mt-10 flex items-center justify-center gap-4 flex-wrap">
                        <Link to="/prayers" data-testid="cta-pray-today" className="px-8 py-3 bg-liturgy text-white text-sm tracking-wide hover:opacity-90 transition-opacity duration-300">
                            Pray with us today
                        </Link>
                        <Link to="/companion" data-testid="cta-meet-companion" className="px-8 py-3 border border-liturgy accent-liturgy text-sm tracking-wide hover:bg-liturgy hover:text-white transition-colors duration-300">
                            Meet the Companion
                        </Link>
                    </div>
                </div>
            </section>

            {/* NEWSLETTER STRIP — visible high on the page */}
            <section className="max-w-5xl mx-auto px-6 md:px-10 pt-10 -mt-6">
                <NewsletterForm variant="strip" title="Walk with us — a weekly letter" ctaLabel="Subscribe" />
            </section>

            {/* TODAY GRID */}
            <section className="max-w-6xl mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
                {/* Readings */}
                <div className="md:col-span-7 space-y-8">
                    <div>
                        <div className="divider-ornament small-caps text-xs mb-6">Today's Readings</div>
                        <div className="missal-card p-8 md:p-10">
                            {today.readings && (
                                <div className="space-y-6">
                                    <div>
                                        <div className="small-caps text-[11px] accent-liturgy mb-2">First Reading — {today.readings.first_reading.citation}</div>
                                        <p className="font-body leading-relaxed text-[15px]">{today.readings.first_reading.text}</p>
                                    </div>
                                    <div>
                                        <div className="small-caps text-[11px] accent-liturgy mb-2">Responsorial Psalm — {today.readings.responsorial_psalm.citation}</div>
                                        <p className="font-body italic leading-relaxed text-[15px]">{today.readings.responsorial_psalm.text}</p>
                                    </div>
                                    <div>
                                        <div className="small-caps text-[11px] accent-liturgy mb-2">Gospel — {today.readings.gospel.citation}</div>
                                        <p className="font-body leading-relaxed text-[15px]">{today.readings.gospel.text}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="divider-ornament small-caps text-xs mb-6">Reflection for the Day</div>
                        <blockquote className="border-l-2 border-liturgy pl-6 py-2 font-body italic text-lg leading-relaxed text-foreground" data-testid="daily-reflection">
                            {today.readings?.reflection}
                        </blockquote>
                    </div>
                </div>

                {/* Sidebar: Prayer of the day */}
                <aside className="md:col-span-5 space-y-8">
                    <div className="missal-card p-8">
                        <div className="small-caps text-[11px] accent-liturgy mb-3">Prayer of the Day</div>
                        {featured ? (
                            <>
                                <h2 className="font-serif text-2xl mb-3" data-testid="featured-prayer-title">{featured.title.en}</h2>
                                <p className="text-sm text-muted-foreground italic mb-5">{featured.description}</p>
                                <div className="mb-5">
                                    <AudioPlayer slug={featured.slug} language="en" voice={featured.voice} />
                                </div>
                                <Link to={`/prayers/${featured.slug}`} data-testid="featured-prayer-link" className="text-sm accent-liturgy hover:underline underline-offset-4">
                                    Read this prayer in Latin & Spanish →
                                </Link>
                            </>
                        ) : <div className="text-muted-foreground text-sm">Loading…</div>}
                    </div>

                    <div className="missal-card p-8">
                        <div className="small-caps text-[11px] accent-liturgy mb-3">The Companion</div>
                        <div className="flex items-start gap-4">
                            <img src="/companion.jpg" alt="A Friend from Scripture" className="w-20 h-20 object-cover rounded-sm border border-border" />
                            <div>
                                <p className="text-sm leading-relaxed">A friend from Scripture waits for you. Ask a question, hear the Word spoken aloud, or simply sit in his company.</p>
                                <Link to="/companion" data-testid="companion-teaser-link" className="mt-3 inline-block text-sm accent-liturgy hover:underline underline-offset-4">
                                    Enter the room →
                                </Link>
                            </div>
                        </div>
                    </div>
                </aside>
            </section>

            {/* THREE PATHS */}
            <section className="max-w-6xl mx-auto px-6 md:px-10 pb-24">
                <div className="divider-ornament small-caps text-xs mb-10">Three paths for today</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[
                        { to: "/wall", title: "Leave an intention", body: "A candle carried by your fellow children of Abba Father. Anonymous, if you wish.", testid: "path-wall" },
                        { to: "/prayers", title: "Sit with a prayer", body: "Rosary, Anima Christi, the Our Father in Latin — prayed deeply from the heart.", testid: "path-prayers" },
                        { to: "/lead-magnets", title: "Take a gift home", body: "A printable prayer journal and the free companion app.", testid: "path-gifts" },
                    ].map(c => (
                        <Link key={c.to} to={c.to} data-testid={c.testid} className="missal-card p-8 hover-lift block">
                            <div className="accent-liturgy small-caps text-[11px] mb-3">{c.title}</div>
                            <p className="text-base font-body leading-relaxed">{c.body}</p>
                            <div className="mt-6 text-xs accent-liturgy">Enter →</div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* NEWSLETTER — quiet Sunday letter (kept at bottom for those who scroll) */}
            <section className="max-w-4xl mx-auto px-6 md:px-10 pb-24">
                <div className="divider-ornament small-caps text-xs mb-10">A letter, once a week</div>
                <NewsletterForm />
            </section>
        </div>
    );
};

export default Home;
